<template>
  <div class="diagnostics-header">
    {{ tr('Diagnostics')
    }}
    <div class="files-info" @click="openFiles()">
      <span class="material-symbols-outlined icon-button-to-primary"
        :title="tr('Files and Upload')">folder_shared</span><span class="file-count" v-if="filesCount">{{ filesCount }}
      </span>
    </div>
  </div>
  <div class="tab-container">
    <!--  This will be coming back later, but hidden for now, until a decision is made on how to move forward with it.
      <div class="tab" :class="{ 'tab-active': tabAllActive }" id="diag-all-tab" @click="tabDiagClick('All')">{{
      tr('All')
    }}
      <span class="tab-count"
        v-if="genomicsCount + pathologyCount + radiologyCount">{{
          genomicsCount + pathologyCount + radiologyCount }} </span>
    </div>-->
    <div class="tab" :class="{ 'tab-active': tabGenActive }" id="diag-gen-tab" @click="tabDiagClick('Gen')">
      {{ tr('Genomics') }}
      <span class="tab-count" v-if="genomicsCount">{{ genomicsCount }} </span>
    </div>
    <div class="tab" :class="{ 'tab-active': tabPathActive }" id="diag-path-tab" @click="tabDiagClick('Path')">
      {{ tr('Pathology') }} <span class="tab-count" v-if="pathologyCount">{{ pathologyCount }} </span>
    </div>
    <div class="tab" :class="{ 'tab-active': tabRadActive }" id="diag-rad-tab" @click="tabDiagClick('Rad')">
      {{ tr('Radiology') }}
      <span class="tab-count" v-if="radiologyCount">{{ radiologyCount }} </span>
    </div>
  </div>

  <div class="diag-list-container">
    <div class="diag-container" :class="{ 'diag-container-active': tabAllActive }" id="diag-all-list">All</div>
    <div class="diag-container" :class="{ 'diag-container-active': tabGenActive }" id="diag-gen-list">
      <span class="genomics-header-label">{{ tr('Genomics') }}</span>
      <div class="genomics-specimens">
        <SpecimensCard @count="setCount"></SpecimensCard>
      </div>
      <span class="genomics-disclaimer">
        {{ tr('The test results displayed are based on the' +
          ' data that was made available to the datma system') }}
      </span>
      <div class="genomics-compare-box">
        <button class="primary-filled-button">{{ tr('Compare') }}</button>
      </div>
    </div>
    <div class="diag-container" :class="{ 'diag-container-active': tabPathActive }" id="diag-path-list">
      <span class="genomics-header-label">{{ tr('Pathology') }}</span>
      <div class="genomics-specimens">
        <PathologyCard @onManifestCountChange="setPathologyCount"></PathologyCard>
      </div>
    </div>
    <div class="diag-container" :class="{ 'diag-container-active': tabRadActive }" id="diag-rad-list">Radiology</div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import getEnv from '@/utils/env'
import { fetchWithTimeout } from '@/common/shared'
import SpecimensCard from '@/ux3/components/Genomics/Specimens/SpecimensCard.vue'
import PathologyCard from '@/ux3/components/Pathology/Specimens/PathologyCard.vue'
import { clearCenterColumn } from '@/ux3/components/CenterColumnManagement/centerColumnManagement.js'

const store = useStore()

const tr = (x) => x // inject('tr')
const tabAllActive = ref(false)
const tabGenActive = ref(true)
const tabPathActive = ref(false)
const tabRadActive = ref(false)
const genomicsCount = ref(0)
const radiologyCount = ref(0)
const pathologyCount = ref(0)
const filesCount = ref(0)

const isCenterColumnOpen = computed(() => !!store.getters.session?.centerColumnState?.container)
const areReportsOpen = computed(() => !!store.getters.session?.centerColumnState?.reports)

const fileBaseUrl = getEnv('VUE_APP_FILING_CABINET_LIST_URL') || '/ehr_crud/api/v1/report-store/patient-identifier/'

const clearDiagTabs = () => {
  tabAllActive.value = false
  tabGenActive.value = false
  tabPathActive.value = false
  tabRadActive.value = false
}

const tabLookup = { 'All': tabAllActive, 'Gen': tabGenActive, 'Path': tabPathActive, 'Rad': tabRadActive }
const tabDiagClick = (tab) => {
  clearDiagTabs()
  tabLookup[tab].value = true
}

const getReportListWithTimeout = async (patientId, fileBaseUrl) => {
  try {
    const fetchParams = {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ' + store.getters.jwt,
      },
    }
    const url = `${fileBaseUrl}${patientId.value}`
    const response = await fetchWithTimeout(url, fetchParams)
    if (!response.ok) {
      console.log(`error: status=${response.status}`)
      return []
    }
    const result = await response.json()
    if (!result) { return [] }
    // Translate the response to the format expected by the frontend.
    return result.reports
  } catch (e) {
    if (e.name === 'AbortError') {
      console.log('timeout error')
    } else {
      console.log('ERROR:', e)
    }
  }
  return []
}

const patientId = computed(() => store.getters.session?.patient?.id || '')

onMounted(async () => {
  // TODO: get info for diagnostics and put in cards
  if (patientId.value) {
    const result = await getReportListWithTimeout(patientId, fileBaseUrl)
    filesCount.value = result?.length || 0
  }
})

const openFiles = () => {
  isCenterColumnOpen.value = true
  areReportsOpen.value = true
  clearCenterColumn('reports')
}

const setCount = (n) => {
  genomicsCount.value = n
}

const setPathologyCount = (n) => {
  pathologyCount.value = n
}


watch(patientId, async () => {
  const refresh = await getReportListWithTimeout(patientId, fileBaseUrl)
  filesCount.value = refresh?.length || 0
})


</script>

<style lang="scss" scoped>
.diag-list-container {
  height: calc(90vh - 100px);
}

.genomics-specimens {
  margin-top: 2%;
  height: calc(80vh - 100px);
  width: 100%;
  overflow-y: auto;
}

.diagnostics-header {
  color: var(--Grey600);
  padding: 1rem 1.4rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--Grey200);
}

.diag-container {
  display: none;
  padding: 15px;
}

.diag-container-active {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.diag-container {
  overflow: auto;
}

.genomics-disclaimer {
  margin-top: 15px;
  font-size: 12px;
  color: var(--Grey500);
  bottom: 0;
  left: 0;
}

.genomics-header-label {
  text-align: center;
}

.genomics-compare-box {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: right;
}

.genomics-compare-button {
  font-size: 70%;
  margin-top: 10px;
  margin-right: 45px;
  border-radius: 4px;
  width: 80px;
  height: 35px;
  border: none;
  cursor: pointer;
  text-align: center;
  transition: all linear 200ms;

  &:hover {
    background-color: var(--Grey300);
  }
}

.files-info {
  display: flex;
}

.files-upload-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  span {
    padding-left: 8px;
  }
}

.file-count {
  display: flex;
  height: 15px;
  min-width: 15px;
  padding: 5px;
  color: #fff;
  background-color: var(--Secondary);
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  font-weight: 800;
  border-radius: 20px;
  margin-left: -11px;
  margin-top: 3px;
}

.tab-count {
  display: flex;
  height: 15px;
  min-width: 15px;
  padding: 5px;
  color: #fff;
  background-color: var(--Primary);
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  font-weight: 800;
  border-radius: 20px;
  margin-left: 0;
  margin-top: -12px;
}
</style>
